import React from 'react';
import { useTranslation } from 'react-i18next';
import dayjs, { type Dayjs } from 'dayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

interface FormDatePickerProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  hint?: string;
  required?: boolean;
  minDate?: Dayjs;
  maxDate?: Dayjs;
  disableFuture?: boolean;
  id?: string;
  /** Stable key for scroll-to-error. Rendered as `data-field` on the wrapper. */
  fieldKey?: string;
}

/**
 * Themed wrapper around MUI's `DatePicker` that conforms to the
 * `FormInput` look-and-feel (haat-red accent, rounded corners, error
 * state) and serializes its value to a `YYYY-MM-DD` string so it slots
 * directly into the existing string-based onboarding state.
 */
const FormDatePicker: React.FC<FormDatePickerProps> = ({
  label,
  value,
  onChange,
  error,
  hint,
  required,
  minDate,
  maxDate,
  disableFuture = true,
  id,
  fieldKey,
}) => {
  const { t } = useTranslation();
  const errorMessage = error
    ? t(`validation.${error}`, { defaultValue: error })
    : undefined;

  const parsed = value ? dayjs(value) : null;
  const safeValue = parsed && parsed.isValid() ? parsed : null;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <div
        className="flex w-full scroll-mt-24 flex-col gap-1.5"
        data-field={fieldKey}
      >
        <label htmlFor={id} className="text-sm font-medium text-gray-700">
          {label}
          {required && <span className="text-haat-red-600 ms-1">*</span>}
        </label>
        <DatePicker
          value={safeValue}
          onChange={(next) => {
            onChange(next && next.isValid() ? next.format('YYYY-MM-DD') : '');
          }}
          format="DD/MM/YYYY"
          minDate={minDate}
          maxDate={maxDate}
          disableFuture={disableFuture}
          slotProps={{
            textField: {
              id,
              fullWidth: true,
              size: 'medium',
              error: !!error,
              sx: {
                '& .MuiOutlinedInput-root': {
                  borderRadius: '0.75rem',
                  backgroundColor: '#fff',
                  fontSize: '0.95rem',
                  '& fieldset': { borderColor: '#e5e7eb' },
                  '&:hover fieldset': {
                    borderColor: 'rgba(191, 0, 53, 0.3)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#bf0035',
                    borderWidth: '2px',
                  },
                  '&.Mui-error fieldset': { borderColor: '#dc2626' },
                },
                '& .MuiOutlinedInput-input': { padding: '12px 14px' },
              },
            },
            day: {
              sx: {
                '&.Mui-selected': {
                  backgroundColor: 'rgba(191, 0, 53, 0.92)',
                  '&:hover': { backgroundColor: '#bf0035' },
                  '&:focus': { backgroundColor: '#bf0035' },
                },
              },
            },
          }}
        />
        {errorMessage ? (
          <span className="text-xs font-medium text-red-600">{errorMessage}</span>
        ) : hint ? (
          <span className="text-xs text-gray-500">{hint}</span>
        ) : null}
      </div>
    </LocalizationProvider>
  );
};

export default FormDatePicker;
